import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { localStorageKeys } from '../../utils/constants';
import './UploadMenuImage.css';

const API_BASE = process.env.REACT_APP_KITCHEN_BACKEND_API_URL;
const UPLOAD_API_URL = `${API_BASE}/UploadMenuImageToCloudinary`;
const MAX_FILES = 10;
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png'];
const ONBOARDING_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function deriveFileName(file) {
  return (
    file.name
      .replace(/\.[^.]+$/, '')
      .replace(/[^a-zA-Z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase()
      .slice(0, 50) || 'menu-image'
  );
}

function getAcceptString(allowedTypes) {
  return allowedTypes.join(',');
}

function getHintText(allowedTypes) {
  const parts = [];
  if (allowedTypes.includes('image/jpeg') || allowedTypes.includes('image/png')) {
    parts.push('JPEG, PNG');
  }
  if (allowedTypes.includes('application/pdf')) {
    parts.push('PDF');
  }
  return `${parts.join(', ')} only · max 5 MB each · up to ${MAX_FILES} files`;
}

const MenuFileUpload = ({
  variant = 'default',
  allowPdf = false,
  showUploadButton = true,
  onUploadSuccess,
  onFilesChange,
  uploadButtonLabel,
}) => {
  const allowedTypes = allowPdf ? ONBOARDING_ALLOWED_TYPES : DEFAULT_ALLOWED_TYPES;
  const isMonochrome = variant === 'monochrome';

  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadBanner, setUploadBanner] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    onFilesChange?.(files.map((item) => item.file));
  }, [files, onFilesChange]);

  const kitchenName = useMemo(() => {
    try {
      const token = localStorage.getItem(localStorageKeys.TOKEN);
      if (!token) return '';
      const { kitchenName: kn } = jwtDecode(token);
      return kn || '';
    } catch {
      return '';
    }
  }, []);

  const addFiles = useCallback(
    (rawFiles) => {
      setError('');
      setUploadBanner('');
      const incoming = Array.from(rawFiles);
      const validationErrors = [];

      const valid = incoming.filter((f) => {
        if (!allowedTypes.includes(f.type)) {
          validationErrors.push(`"${f.name}" — file type not allowed`);
          return false;
        }
        if (f.size > MAX_SIZE_BYTES) {
          validationErrors.push(`"${f.name}" — exceeds the 5 MB limit`);
          return false;
        }
        return true;
      });

      const remaining = MAX_FILES - files.length;
      if (valid.length > remaining) {
        validationErrors.push(
          `Maximum ${MAX_FILES} files allowed — only ${remaining} more can be added`
        );
        valid.splice(remaining);
      }

      if (validationErrors.length) setError(validationErrors.join('. '));

      const items = valid.map((f) => ({
        id: Math.random().toString(36).slice(2),
        file: f,
        previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
        isPdf: f.type === 'application/pdf',
      }));

      setFiles((prev) => [...prev, ...items]);
    },
    [allowedTypes, files.length]
  );

  const removeFile = useCallback((id) => {
    setFiles((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearSelection = useCallback(() => {
    files.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setFiles([]);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [files]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleUpload = useCallback(async () => {
    if (!files.length) return false;
    setUploading(true);
    setError('');
    setUploadBanner('');

    try {
      const formData = new FormData();
      formData.append('kitchenName', kitchenName);

      const fileNames = files.map((item) => deriveFileName(item.file));

      if (files.length === 1) {
        formData.append('fileName', fileNames[0]);
        formData.append('imageType', 'menu');
      } else {
        formData.append('fileNames', JSON.stringify(fileNames));
        formData.append('imageTypes', JSON.stringify(files.map(() => 'menu')));
      }

      files.forEach((item) => formData.append('file', item.file));

      const token = localStorage.getItem(localStorageKeys.TOKEN);
      const res = await fetch(UPLOAD_API_URL, {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = await res.json();

      if (data.success) {
        setUploadBanner(
          `${data.message} · ${data.totalImages} total file${data.totalImages !== 1 ? 's' : ''} on file`
        );
        clearSelection();
        onUploadSuccess?.(data);
        return true;
      }

      setError(data.message || 'Upload failed. Please try again.');
      return false;
    } catch (err) {
      setError(err?.message || 'Network error. Please check your connection and try again.');
      return false;
    } finally {
      setUploading(false);
    }
  }, [files, kitchenName, clearSelection, onUploadSuccess]);

  const dropZoneClass = [
    'umi-drop-zone',
    isMonochrome ? 'umi-drop-zone--mono' : '',
    isDragOver ? 'umi-drop-zone--active' : '',
    isMonochrome && isDragOver ? 'umi-drop-zone--mono-active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const browseBtnClass = isMonochrome ? 'umi-browse-btn umi-browse-btn--mono' : 'umi-browse-btn';
  const ctaBtnClass = isMonochrome ? 'umi-cta-btn umi-cta-btn--mono' : 'umi-cta-btn';

  return (
    <div className={isMonochrome ? 'umi-upload--mono' : ''}>
      {uploadBanner && <div className='umi-success-banner umi-success-banner--mono'>{uploadBanner}</div>}

      <div
        className={dropZoneClass}
        role='button'
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept={getAcceptString(allowedTypes)}
          multiple
          className='umi-hidden'
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className='umi-drop-prompt'>
          <div className={`umi-drop-icon ${isMonochrome ? 'umi-drop-icon--mono' : ''}`}>🖼️</div>
          <p className='umi-drop-title'>Drop files here or click to browse</p>
          <p className='umi-drop-hint'>{getHintText(allowedTypes)}</p>
          <button
            type='button'
            className={browseBtnClass}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Browse files
          </button>
        </div>
      </div>

      {error && (
        <div className='umi-error-banner umi-error-banner--mono'>
          <span className='umi-error-banner__icon'>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {files.length > 0 && (
        <div className='umi-preview-section'>
          <div className='umi-preview-header'>
            <span className='umi-preview-count'>
              {files.length} / {MAX_FILES} file{files.length !== 1 ? 's' : ''} selected
            </span>
            <button type='button' className='umi-clear-btn' onClick={clearSelection}>
              Clear all
            </button>
          </div>
          <div className='umi-grid'>
            {files.map((item) => (
              <div key={item.id} className='umi-grid-item'>
                {item.isPdf ? (
                  <div className='umi-grid-pdf'>PDF</div>
                ) : (
                  <img src={item.previewUrl} alt={item.file.name} className='umi-grid-img' />
                )}
                <div className='umi-grid-overlay'>
                  <p className='umi-grid-name'>{item.file.name}</p>
                  <p className='umi-grid-size'>{formatBytes(item.file.size)}</p>
                </div>
                <button
                  type='button'
                  className='umi-grid-remove'
                  onClick={() => removeFile(item.id)}
                  aria-label='Remove'
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showUploadButton && (
        <button
          type='button'
          className={ctaBtnClass}
          disabled={!files.length || uploading}
          onClick={handleUpload}
        >
          {uploading ? (
            <>
              <span className='umi-spinner umi-spinner--mono' /> Uploading…
            </>
          ) : (
            uploadButtonLabel ||
            `Upload ${files.length > 0 ? `${files.length} file${files.length !== 1 ? 's' : ''}` : 'Files'}`
          )}
        </button>
      )}
    </div>
  );
};

export default MenuFileUpload;
